// Phase 2.8 — Read-only base rate cards modal. Opens when admin clicks the
// "X card(s)" link in the Partners directory Compensation column. No edits
// here — admin still uses the full Edit modal for changes. Goal: spot-check
// rates without losing your place in the directory.

import { Modal } from "@/components/Shared";
import { Pencil } from "lucide-react";

export default function PartnerRateCardsModal({ partner, onClose, onEdit }) {
  if (!partner) return null;
  const cards = (partner.rate_cards || []).slice().sort((a, b) => {
    const gw = (a.gateway || "").localeCompare(b.gateway || "");
    return gw !== 0 ? gw : (a.lob || "").localeCompare(b.lob || "");
  });

  return (
    <Modal
      open={!!partner}
      onClose={onClose}
      title={`Base rate cards · ${partner.partner_name}`}
      testid="partner-rate-cards-modal"
      size="md"
    >
      <div className="space-y-3" data-testid="rate-cards-content">
        <div className="text-[12.5px] text-[var(--muted)] leading-relaxed">
          Current base rates for <b className="text-[var(--ink)]">{partner.partner_id}</b>.
          These are the floor rates BizAssist invoices the partner at —
          commission = (merchant rate − base rate) × volume for each Gateway × LOB.
        </div>

        {cards.length === 0 ? (
          <div className="text-center text-[12.5px] text-[var(--muted)] py-8 border border-dashed border-[var(--line-2)] rounded-sm">
            No rate cards configured yet.{" "}
            <button onClick={onEdit} data-testid="rate-cards-empty-edit" className="text-[var(--accent)] hover:underline">Add one →</button>
          </div>
        ) : (
          <div className="border border-[var(--line)] rounded-sm overflow-hidden">
            <table className="w-full text-[12.5px]">
              <thead className="bg-[var(--surface-2)] text-[10px] uppercase tracking-wider text-[var(--muted)]">
                <tr>
                  <th className="text-left py-2 px-3">Gateway</th>
                  <th className="text-left py-2 px-3">LOB</th>
                  <th className="text-right py-2 px-3">Base rate</th>
                </tr>
              </thead>
              <tbody>
                {cards.map((c) => (
                  <tr key={`${c.gateway}-${c.lob}`} className="border-t border-[var(--line)]" data-testid={`rc-row-${c.gateway}-${c.lob}`}>
                    <td className="py-2 px-3 font-medium">{c.gateway || <span className="text-[var(--muted)]">—</span>}</td>
                    <td className="py-2 px-3">{c.lob || <span className="text-[var(--muted)]">—</span>}</td>
                    <td className="py-2 px-3 text-right num font-semibold text-[var(--accent)]">
                      {(Number(c.partner_base_rate || 0) * 100).toFixed(4).replace(/\.?0+$/, "")}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="text-[10.5px] text-[var(--muted)] italic">
          Need to change a rate? Use <b>Edit partner → Details</b> — that flow
          also captures the effective-from date and writes a versioned audit row.
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} data-testid="rate-cards-close" className="inline-flex items-center justify-center gap-1.5 h-9 px-3.5 text-[12.5px] font-medium border border-[var(--line-2)] rounded-sm hover:bg-[var(--surface-2)]">Close</button>
        <button onClick={onEdit} data-testid="rate-cards-edit" className="inline-flex items-center justify-center gap-1.5 h-9 px-3.5 text-[12.5px] font-medium bg-[var(--ink)] text-white rounded-sm hover:opacity-90">
          <Pencil size={12} /> Open in editor
        </button>
      </div>
    </Modal>
  );
}
